import { BackButton, Loader } from "@src/components/atoms";
import EventDetailsCard from "@src/components/cards/operator/EventDetailsCard";
import EventSavingsCard from "@src/components/cards/operator/EventSavingsCard";
import AppUtils from "@src/utils/appUtils";
import HorizontalResponsiveLayout from "@src/components/cards/HorizontalResponsiveLayout";
import Tabs from "@src/components/atoms/Tabs";
import { getBehaviourColourForPosition } from "@signolteam/shared/colours";
import { useOperatorContext } from "@src/context/operatorContext";
import OperatorHooks from "@src/context/hooks/operatorHooks";
import BehaviourResult from "./BehaviourResult";
import moment from "moment";
import { useDCContext } from "@src/context/dcContext";
import { Icon } from "@src/components/atoms";
import { trackActionActivity } from "@src/utils/userActivityTracker";
import ErrorPage from "@src/components/layout/ErrorPage";

const EventDetailsPage = ({ overrideEventId }) => {
  const { profile, selectedBehaviour, setSelectedBehaviour } =
    useOperatorContext();
  const dc = useDCContext();
  const { customer, operator } = profile;
  const { getDynamicReplacement } = dc;

  const eventId = overrideEventId ?? location.pathname.split("/").pop();
  //Load event by id
  const {
    data: { event } = {},
    isPending: eventPending,
    error: eventError,
  } = OperatorHooks.useOperatorEventById(operator?.id, eventId);

  if (!profile || !!eventError) {
    return <ErrorPage pageName={"event details"} />;
  }
  const getContentForBehaviour = (b, m, comment, bColor, isOpen) => {
    const formattedMetadata = {};
    for (const i of m) {
      formattedMetadata[i["key"]] = i["value"];
    }

    return (
      <div id={"tab-id"} className="h-full">
        <BehaviourResult
          behaviour={b}
          comment={comment}
          customer={customer}
          metadata={formattedMetadata}
          bColor={bColor}
          isOpen={isOpen}
          event={event}
        />
      </div>
    );
  };

  const getTabs = (event) => {
    const data = event.behaviours.map((b) => {
      const orderColor = getBehaviourColourForPosition(b.order);
      const neutralColor = getBehaviourColourForPosition(0);
      const hasReason = b?.reasons?.length > 0;
      const wasFiltered =
        !!hasReason && b?.reasons?.[0]?.startsWith("NOT_POSSIBLE_");

      const isOpen = AppUtils.isInOpenReportingRange(
        moment.utc(event.ocurrenceDatetime),
        customer.reportingDelay,
        customer.targetPeriodDuration,
      );
      const wasPastSelfReportable =
        !isOpen && b?.isSelfReportable && b?.succeededSelfReported === null;

      const bColor =
        wasFiltered || wasPastSelfReportable ? neutralColor : orderColor;
      const comment = event.comments.filter((c) => c.behaviourId === b.id);
      return {
        name: b.behaviourAcronym,
        colour: bColor.name,
        order: b.order,
        content: getContentForBehaviour(
          b,
          event.metadata,
          comment.length > 0 ? comment[0].comment : null,
          bColor,
          isOpen,
        ),
      };
    });
    return data.sort((a, b) => a.order > b.order);
  };

  return (
    <section className="p-0 h-full">
      {eventPending ? (
        <div className="min-h-120">
          <div className="w-full h-full flex items-center justify-center">
            <Loader size="2xl" />
          </div>
        </div>
      ) : eventError ? (
        <ErrorPage pageName={"event details"} />
      ) : (
        <div className="flex flex-col h-full">
          <div className="p-2">
            <BackButton trackingActionKey="BACK_TO_EVENT_LIST" />
          </div>
          {!event ? (
            <ErrorPage pageName={"event details"} />
          ) : (
            <>
              <div>
                <HorizontalResponsiveLayout
                  horizontal={{ ratio: [1, 1] }}
                  vertical={{ className: "p-2 mb-4" }}
                  gap={4}
                >
                  <EventDetailsCard
                    event={event}
                    industry={customer.industry}
                    customerCode={customer.customerCode}
                  />
                  <EventSavingsCard
                    getDynamicContent={getDynamicReplacement}
                    event={event}
                    industry={customer.industry}
                  />
                </HorizontalResponsiveLayout>
              </div>
              <Tabs
                tabs={getTabs(event)}
                gap={true}
                layout={"spread"}
                selected={
                  selectedBehaviour
                    ? getTabs(event).findIndex(
                        (t) => t.name === selectedBehaviour?.behaviourAcronym,
                      )
                    : 0
                }
                onChange={(idx) => {
                  const newBehaviourAcronym = getTabs(event)?.[idx]?.name;
                  if (newBehaviourAcronym) {
                    trackActionActivity("EVENT_DETAILS_OPEN_BEHAVIOUR", {
                      newValue: newBehaviourAcronym,
                    });
                    const newBehaviour = event.behaviours.find(
                      (b) => b.behaviourAcronym === newBehaviourAcronym,
                    );
                    if (newBehaviour) {
                      setSelectedBehaviour(newBehaviour);
                    }
                  }
                }}
              />
            </>
          )}
        </div>
      )}
    </section>
  );
};

export const eventDetailsPageControls = {};

EventDetailsPage.propTypes = AppUtils.getPropTypeFromControls(
  eventDetailsPageControls,
);

export default EventDetailsPage;
